/* eslint-disable react/prop-types, prettier/prettier */
import React, { useRef, useCallback, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import { useNavigate } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import useAuth from '../../hooks/useAuth'
import { canOpenPath } from '../../helpers/routeAccess'
import useCommsManager from '../../hooks/useCommsManager'
// A cycle (routes.js renders this page) that is safe: the table is read
// while rendering, after both modules have loaded, never at import time.
import routes from '../../routes/routes'
import {
    activePipelinesLabel,
    useActivePipelineCount,
} from '../../hooks/usePipelineCount'
import {
    ACCENT,
    CATEGORY,
    RADIUS,
    SHADOW,
    STATUS,
    SURFACE,
    TEXT,
    pageSx,
    tint,
} from '../../theme/tokens'

const floatUp = keyframes`
  0%   { transform: translateY(0)    scale(1);    opacity: 0.7; }
  100% { transform: translateY(-70px) scale(0.2); opacity: 0; }
`
const rippleOut = keyframes`
  0%   { width: 0;     height: 0;     opacity: 0.45; }
  100% { width: 360px; height: 360px; opacity: 0; }
`

const OPS_MODULES = [
    {
        title: 'Pipelines',
        icon: '⟳',
        path: '/pipelines',
        subtitle: 'Manage event-triggered comm pipelines',
    },
    {
        title: 'Templates',
        icon: '▣',
        path: '/templates',
        subtitle: 'Browse and approve comm templates',
    },
    {
        title: 'Create Template',
        icon: '✏',
        path: '/create-template',
        subtitle: 'Build a template, or start an email from a .tmpl file',
        // The route needs the comms manager role (hooks/useCommsManager).
        manager: true,
    },
    {
        title: 'Bulk Send',
        icon: '▶',
        path: '/bulk-send',
        subtitle: 'Send to a CSV audience in one go',
    },
    {
        title: 'Reports',
        icon: '≡',
        path: '/reports',
        subtitle: 'Delivery and engagement analytics',
    },
    {
        title: 'Holidays',
        icon: '▦',
        path: '/holidays',
        subtitle: 'Holiday comms and their dates',
    },
    {
        title: 'Settings',
        icon: '⚙',
        path: '/settings',
        subtitle: 'Platform configuration',
    },
]

// Each AI module's identity colour (CATEGORY: text-safe on the card and on
// its own tint in both themes; contrast.ai-a.test.js).
const MODULES = [
    {
        area: 'tw',
        title: 'Template Writer',
        status: 'live',
        color: CATEGORY.orange,
        icon: '✦',
        path: '/ai/template-writer',
        subtitle: 'Draft production-ready templates from a plain brief',
        desc: 'Describe the goal, pick the channel. AI produces a complete, variable-tagged WhatsApp, Email, or SMS template in seconds — no copywriter required.',
    },
    {
        area: 'csv',
        title: 'CSV Validator',
        status: 'live',
        color: CATEGORY.blue,
        icon: '⊞',
        path: '/ai/csv-validator',
        subtitle: 'Catch errors before anything is sent',
        desc: null,
    },
    {
        area: 'bulk',
        title: 'Bulk Preview',
        status: 'live',
        color: CATEGORY.amber,
        icon: '◉',
        path: '/ai/bulk-preview',
        subtitle: 'See every personalised message rendered before send',
        desc: null,
    },
    {
        area: 'cb',
        title: 'Campaign Brief',
        status: 'live',
        color: CATEGORY.orange,
        icon: '◈',
        path: '/ai/campaign-brief',
        subtitle: 'One sentence in → full campaign strategy out',
        desc: 'Persona, best send time, template, KPIs, and follow-up plan — all generated from a plain-text goal.',
    },
    {
        area: 'pi',
        title: 'Perf. Insights',
        status: 'live',
        color: CATEGORY.green,
        icon: '↗',
        path: '/ai/performance-insights',
        subtitle: 'Score campaigns and surface actionable fixes',
        desc: null,
    },
    {
        area: 'ab',
        title: 'A/B Variants',
        status: 'live',
        color: CATEGORY.blue,
        icon: 'A|B',
        path: '/ai/ab-variants',
        subtitle: '3 tonal variants from one goal — instantly comparable',
        desc: null,
    },
    {
        area: 'ur',
        title: 'Unsubscribe Risk',
        status: 'live',
        color: CATEGORY.pink,
        icon: '⊘',
        path: '/ai/unsubscribe-risk',
        subtitle: 'Score opt-out risk before you send',
        desc: 'Detects urgency overload, missing opt-out lines, and spammy patterns. Returns a 0–100 risk score with a safer revised snippet.',
    },
    {
        area: 'ao',
        title: 'Audience Overlap',
        status: 'live',
        color: CATEGORY.green,
        icon: '⊛',
        path: '/ai/audience-overlap',
        subtitle: 'Detect over-targeted recipients before send',
        desc: null,
    },
    {
        area: 'crm',
        title: 'CRM Enrich',
        status: 'live',
        color: CATEGORY.amber,
        icon: '🔗',
        path: '/ai/crm-enrich',
        subtitle: 'Resolve Salesforce IDs without leaving the platform',
        desc: null,
    },
    {
        area: 'cf',
        title: 'Comms Finder',
        status: 'live',
        color: CATEGORY.blue,
        icon: '🔍',
        path: '/ai/comms-finder',
        subtitle: 'Natural-language search across pipelines and history',
        desc: "Describe what you're looking for — pipelines, templates, and send history returned in one view. No filter syntax, no exact names.",
    },
]

function MagicCard({ module, onClick }) {
    const cardRef = useRef(null)
    const [ripples, setRipples] = useState([])
    const [hovered, setHovered] = useState(false)
    const { area, title, subtitle, icon, color, status, desc } = module

    const particles = useMemo(
        () =>
            Array.from({ length: 10 }, (_, i) => ({
                id: i,
                left: `${5 + i * 9.5}%`,
                bottom: `${10 + (i % 4) * 18}%`,
                size: 1.5 + (i % 3),
                delay: i * 0.11,
                dur: 0.9 + (i % 3) * 0.25,
            })),
        []
    )

    const handleMouseMove = useCallback((e) => {
        if (!cardRef.current) return
        const r = cardRef.current.getBoundingClientRect()
        const x = e.clientX - r.left
        const y = e.clientY - r.top
        const cx = r.width / 2
        const cy = r.height / 2
        cardRef.current.style.setProperty('--mx', `${x}px`)
        cardRef.current.style.setProperty('--my', `${y}px`)
        cardRef.current.style.setProperty('--rx', `${((y - cy) / cy) * -5}deg`)
        cardRef.current.style.setProperty('--ry', `${((x - cx) / cx) * 5}deg`)
    }, [])

    const handleMouseLeave = useCallback(() => {
        setHovered(false)
        if (!cardRef.current) return
        cardRef.current.style.setProperty('--rx', '0deg')
        cardRef.current.style.setProperty('--ry', '0deg')
    }, [])

    const isShipping = status === 'shipping'

    const handleClick = useCallback(
        (e) => {
            if (isShipping) return
            if (!cardRef.current) return
            const r = cardRef.current.getBoundingClientRect()
            const id = Date.now()
            setRipples((prev) => [
                ...prev,
                { x: e.clientX - r.left, y: e.clientY - r.top, id },
            ])
            setTimeout(
                () => setRipples((prev) => prev.filter((rp) => rp.id !== id)),
                900
            )
            onClick()
        },
        [onClick, isShipping]
    )

    const handleKeyDown = useCallback(
        (e) => {
            if (isShipping) return
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
            }
        },
        [onClick, isShipping]
    )

    const isWide = desc !== null

    const card = (
        <Box
            ref={cardRef}
            role="button"
            tabIndex={0}
            aria-disabled={isShipping || undefined}
            aria-label={isShipping ? `${title} — coming soon` : title}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            sx={{
                gridArea: area,
                position: 'relative',
                background: SURFACE.card,
                border: `1px solid ${SURFACE.border}`,
                borderRadius: `${RADIUS.lg}px`,
                boxShadow: SHADOW.card,
                cursor: isShipping ? 'default' : 'pointer',
                opacity: isShipping ? 0.6 : 1,
                overflow: 'hidden',
                transformStyle: 'preserve-3d',
                transform:
                    'perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg))',
                transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                willChange: 'transform',
                p: isWide ? 3 : 2.5,
                minHeight: isWide ? 150 : 115,
                '&:hover': isShipping
                    ? {}
                    : {
                          borderColor: tint(color, 0.33),
                          boxShadow: `${SHADOW.cardHover}, 0 0 0 1px ${tint(
                              color,
                              0.13
                          )}`,
                      },
                '&:focus-visible': {
                    outline: `2px solid ${ACCENT.border55}`,
                    outlineOffset: 2,
                },
            }}
        >
            {/* Cursor spotlight */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                    background: `radial-gradient(380px circle at var(--mx, -200px) var(--my, -200px), ${tint(
                        color,
                        0.11
                    )}, transparent 70%)`,
                    opacity: hovered ? 1 : 0,
                    transition: 'opacity 0.35s ease',
                }}
            />

            {/* Floating particles */}
            {particles.map((p) => (
                <Box
                    key={p.id}
                    sx={{
                        position: 'absolute',
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        borderRadius: '50%',
                        background: color,
                        left: p.left,
                        bottom: p.bottom,
                        pointerEvents: 'none',
                        opacity: 0,
                        animation: hovered
                            ? `${floatUp} ${p.dur}s ${p.delay}s ease-out infinite`
                            : 'none',
                    }}
                />
            ))}

            {/* Click ripple */}
            {ripples.map((rp) => (
                <Box
                    key={rp.id}
                    sx={{
                        position: 'absolute',
                        left: rp.x,
                        top: rp.y,
                        width: 0,
                        height: 0,
                        borderRadius: '50%',
                        background: tint(color, 0.16),
                        transform: 'translate(-50%, -50%)',
                        animation: `${rippleOut} 0.85s ease-out forwards`,
                        pointerEvents: 'none',
                    }}
                />
            ))}

            {/* Content */}
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                borderRadius: `${RADIUS.md}px`,
                                flexShrink: 0,
                                background: tint(color, 0.09),
                                border: `1px solid ${tint(color, 0.19)}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                typography: icon.length > 2 ? 'caption' : 'h4',
                                fontWeight: 800,
                                color,
                                transition: 'background 0.2s ease',
                            }}
                        >
                            {icon}
                        </Box>
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    {status === 'shipping' && (
                        <Chip
                            label="Shipping"
                            size="small"
                            sx={{
                                height: 18,
                                typography: 'caption',
                                fontWeight: 700,
                                flexShrink: 0,
                                mt: 0.25,
                                background: STATUS.info.bg,
                                color: STATUS.info.fg,
                                border: `1px solid ${STATUS.info.border}`,
                                borderRadius: `${RADIUS.pill}px`,
                            }}
                        />
                    )}
                </Box>

                <Typography
                    variant="caption"
                    sx={{ color: TEXT.muted, lineHeight: 1.55 }}
                >
                    {subtitle}
                </Typography>

                {isWide && desc && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: TEXT.faint,
                            lineHeight: 1.6,
                            mt: 1,
                            pt: 1,
                            borderTop: `1px solid ${SURFACE.border}`,
                        }}
                    >
                        {desc}
                    </Typography>
                )}
            </Box>
        </Box>
    )

    return isShipping ? (
        <Tooltip title="Coming soon" placement="top" arrow>
            {card}
        </Tooltip>
    ) : (
        card
    )
}

// Exported so routes/routes.test.js can check every card against its route.
export { MODULES as HOME_AI_MODULES, OPS_MODULES as HOME_OPS_LINKS }

/**
 * Home: the AI Suite cards and the operations links, each shown only to
 * users whose roles can open its page. The roles are read from the route
 * table itself, so Home and the routes cannot disagree. The Pipelines link
 * says how many pipelines are active.
 *
 * Upcoming Comms (everything scheduled for the next two days) was removed
 * on 2026-10-15: production schedules hundreds of sends a day, so it filled
 * Home with one row per minute and buried the links.
 */
export default function AIIndex() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canManage } = useCommsManager()
    const canOpen = (path) => canOpenPath(routes, path, user?.permissions)
    const modules = MODULES.filter((m) => canOpen(m.path))
    const opsLinks = OPS_MODULES.filter(
        (m) => canOpen(m.path) && (!m.manager || canManage)
    )
    const activeCount = useActivePipelineCount(canOpen('/pipelines'))
    const subtitleOf = (m) =>
        m.path === '/pipelines' && activeCount
            ? activePipelinesLabel(activeCount)
            : m.subtitle

    return (
        <Box
            sx={{
                ...pageSx,
                // A faint dot grid: the text colour at 5%, so it shows on
                // either theme's page without carrying anything.
                backgroundImage: `radial-gradient(${tint(
                    TEXT.primary,
                    0.05
                )} 1px, transparent 1px)`,
                backgroundSize: '24px 24px',
            }}
        >
            <Box
                sx={{
                    maxWidth: 1020,
                    mx: 'auto',
                    p: { xs: 2, md: '40px 32px' },
                }}
            >
                {modules.length > 0 && (
                    <>
                        {/* Header */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                variant="overline"
                                component="p"
                                sx={{ color: ACCENT.text, mb: 1 }}
                            >
                                AI Feature Suite · 10 modules
                            </Typography>
                            <Typography
                                variant="h1"
                                component="p"
                                sx={{ color: TEXT.primary }}
                            >
                                From brief to send.{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        // Orange to its text-safe shade: large
                                        // text, 3:1 on the page at both stops
                                        // in both themes (the brand gradient's
                                        // yellow is 1.8:1 on the light page).
                                        background: `linear-gradient(135deg, ${ACCENT.strong}, ${ACCENT.text})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    AI-assisted.
                                </Box>
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: TEXT.faint,
                                    mt: 1.5,
                                    maxWidth: 460,
                                }}
                            >
                                Every stage of the comms workflow — drafting,
                                validating, previewing, analysing, and targeting
                                — has a module.
                            </Typography>
                        </Box>

                        {/* Bento grid */}
                        <Box
                            sx={{
                                display: 'grid',
                                // One column on a phone and two on a tablet: four
                                // columns at 400px left the small cards about 80px
                                // wide, their titles cut off.
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(4, minmax(0, 1fr))',
                                },
                                gridTemplateAreas: {
                                    xs: `"tw" "csv" "bulk" "cb" "pi" "ab" "ur" "ao" "crm" "cf"`,
                                    sm: `
                                "tw   tw"
                                "csv  bulk"
                                "cb   cb"
                                "pi   ab"
                                "ur   ur"
                                "ao   crm"
                                "cf   cf"
                            `,
                                    md: `
                                "tw   tw   csv  bulk"
                                "tw   tw   cb   cb"
                                "pi   ab   ur   ur"
                                "ao   crm  cf   cf"
                            `,
                                },
                                gap: '12px',
                            }}
                        >
                            {modules.map((m) => (
                                <MagicCard
                                    key={m.area}
                                    module={m}
                                    onClick={() => navigate(m.path)}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {/* Operations section */}
                <Box
                    sx={{
                        mt: modules.length > 0 ? 5 : 0,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            height: '1px',
                            background: SURFACE.border,
                        }}
                    />
                    <Typography variant="overline" sx={{ color: ACCENT.text }}>
                        Operations
                    </Typography>
                    <Box
                        sx={{
                            flex: 1,
                            height: '1px',
                            background: SURFACE.border,
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        // minmax(0, 1fr): a plain 1fr track has an `auto`
                        // minimum, so the longest nowrap subtitle forces
                        // ragged column widths.
                        gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: '10px',
                    }}
                >
                    {opsLinks.map((m) => (
                        <Box
                            key={m.title}
                            role="button"
                            tabIndex={0}
                            aria-label={m.title}
                            onClick={() => navigate(m.path)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigate(m.path)
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.75,
                                background: SURFACE.card,
                                border: `1px solid ${SURFACE.border}`,
                                borderRadius: `${RADIUS.md}px`,
                                boxShadow: SHADOW.card,
                                cursor: 'pointer',
                                transition:
                                    'border-color 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    boxShadow: SHADOW.cardHover,
                                    borderColor: ACCENT.border30,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: `${RADIUS.sm}px`,
                                    flexShrink: 0,
                                    background: ACCENT.bg08,
                                    border: `1px solid ${ACCENT.bg15}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    typography: 'body1',
                                    // The glyph is a text character, so it
                                    // takes the text-safe orange (AA on the
                                    // tint; ACCENT.strong was 3.95:1).
                                    color: ACCENT.text,
                                }}
                            >
                                {m.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: TEXT.primary,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {m.title}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    component="p"
                                    sx={{
                                        color: TEXT.faint,
                                        lineHeight: 1.4,
                                        mt: 0.25,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {subtitleOf(m)}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}
